import './App.css';
import { Outlet, useLocation } from 'react-router';
import ScrollToTop from './components/ScrollToTop';
import { AppProvider } from '@toolpad/core/AppProvider';
import { SessionContext } from './SessionContext';
import { Account } from '@toolpad/core/Account';
import AccountMenu from './components/AccountMenu';
import { ApiClientProvider } from './ApiClientProvider';
import { Suspense, useEffect, useState } from 'react';
import { NotificationsProvider, useNotifications } from '@toolpad/core';
import ErrorBoundaryWithNotifications from './components/ErrorBoundaryWithNotifications';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useSessionManagement } from './hooks/sessionManagement';
import { createAppTheme } from './theme/themeConfig';
import { ThemeProvider, Snackbar as MuiSnackbar, type SnackbarProps } from '@mui/material';
import { SplashScreen } from './components/LoadingScreen/SplashScreen';
import { Provider } from 'react-redux';
import store from './redux/store';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { ProactiveRefreshBridge } from './hooks/proactiveRefresh';
import { initGA, logPageView } from './utils/analytics';
import {
    runAfterPageIsInteractive,
    shouldDeferNonCriticalScriptsForRoute,
} from './utils/thirdPartyScripts';
import { IntlProvider } from 'react-intl';
import { ProfileImageSyncer } from './components/ProfileImageSyncer';
import { TermsModalProvider } from './contexts/TermsModalContext';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import SubdomainRouter from './components/SubdomainRouter';
import { Snackbar } from './components/snackbar';

dayjs.locale('en-gb');

function AutoDismissSnackbar(props: SnackbarProps) {
    const { autoHideDuration, ...rest } = props;
    return <MuiSnackbar {...rest} autoHideDuration={autoHideDuration ?? 5000} />;
}

export default function App() {
    const { session, signIn, signOut, sessionContextValue, isLoading } = useSessionManagement();
    const notification = useNotifications();
    const location = useLocation();
    const [companyLogo] = useState<string>("/logo-only.png");
    const locale = typeof window !== 'undefined' ? navigator.language || 'en-GB' : 'en-GB';
    const [theme] = useState(createAppTheme('#493193', '#2fadd8', 'light'));

    useEffect(() => {
        // Initialize Google Analytics in a route-aware manner (browser-only)
        if (typeof window === 'undefined') {
            return;
        }

        const initialiseAnalytics = () => {
            void initGA();
        };

        if (shouldDeferNonCriticalScriptsForRoute(location.pathname)) {
            return runAfterPageIsInteractive(initialiseAnalytics);
        }

        initialiseAnalytics();
        return;
    }, [location.pathname]);

    useEffect(() => {
        // Track page views on route change (browser-only)
        if (typeof window !== 'undefined') {
            logPageView(location.pathname);
        }
    }, [location.pathname]);

    useEffect(() => {
        // Handle unhandled promise rejections (browser-only)
        if (typeof window === 'undefined') {
            return;
        }

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            notification.show(`Unhandled Error: ${event.reason.message || event.reason}`, {
                severity: 'error',
            });
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, [notification]);

    if (isLoading) {
        return <SplashScreen />;
    }

    return (
        <SessionContext.Provider value={sessionContextValue}>
            <ScrollToTop />
            <ProactiveRefreshBridge />
            <Account
                slots={{ popoverContent: AccountMenu }}
                slotProps={{
                    preview: { variant: 'expanded' },
                }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <TermsModalProvider>
                    <ApiClientProvider>
                        <ProfileImageSyncer />
                        <Provider store={store}>
                            <NotificationsProvider slots={{ snackbar: AutoDismissSnackbar }}>
                                <ErrorBoundaryWithNotifications>
                                    <IntlProvider locale={locale}>
                                        <ThemeProvider theme={theme}>
                                            <AppProvider
                                                branding={{
                                                    logo: (
                                                        <img
                                                            src={companyLogo}
                                                            alt="AttendIn Logo"
                                                            style={{ height: '50px' }}
                                                        />
                                                    ),
                                                    title: "AttendIn",
                                                }}
                                                session={session}
                                                authentication={{ signIn, signOut }}
                                                theme={theme}
                                            >
                                                <Suspense fallback={<SplashScreen />}>
                                                    <SubdomainRouter>
                                                        <Outlet />
                                                    </SubdomainRouter>
                                                </Suspense>
                                            </AppProvider>
                                            <TermsAcceptanceModal />
                                            <Snackbar />
                                        </ThemeProvider>
                                    </IntlProvider>
                                </ErrorBoundaryWithNotifications>
                            </NotificationsProvider>
                        </Provider>
                    </ApiClientProvider>
                </TermsModalProvider>
            </LocalizationProvider>

        </SessionContext.Provider>
    );
}
